import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface Review {
  id: number;
  title: string;
  content: string;
  thumbnail_url: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  author: string;
  department: string;
  view_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface ReviewContextType {
  reviewList: Review[];
  loading: boolean;
  addReview: (review: Omit<Review, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateReview: (id: number, review: Partial<Review>) => Promise<void>;
  deleteReview: (id: number) => Promise<void>;
  getReview: (id: number) => Review | undefined;
  incrementViewCount: (id: number) => Promise<void>;
  refreshReviews: () => Promise<void>;
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export const ReviewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [reviewList, setReviewList] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviewList(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const addReview = async (review: Omit<Review, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          ...review,
          view_count: 0
        });

      if (error) throw error;
      await fetchReviews();
    } catch (error) {
      console.error('Error adding review:', error);
      throw error;
    }
  };

  const updateReview = async (id: number, review: Partial<Review>) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ ...review, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  };

  const deleteReview = async (id: number) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setReviewList(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  };

  const getReview = (id: number) => {
    return reviewList.find(r => r.id === id);
  };

  const incrementViewCount = async (id: number) => {
    try {
      const review = reviewList.find(r => r.id === id);
      if (!review) return;

      await supabase
        .from('reviews')
        .update({ view_count: review.view_count + 1 })
        .eq('id', id);

      setReviewList(prev =>
        prev.map(r => r.id === id ? { ...r, view_count: r.view_count + 1 } : r)
      );
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const refreshReviews = async () => {
    await fetchReviews();
  };

  return (
    <ReviewContext.Provider value={{
      reviewList,
      loading,
      addReview,
      updateReview,
      deleteReview,
      getReview,
      incrementViewCount,
      refreshReviews
    }}>
      {children}
    </ReviewContext.Provider>
  );
};

export const useReview = () => {
  const context = useContext(ReviewContext);
  if (context === undefined) {
    throw new Error('useReview must be used within a ReviewProvider');
  }
  return context;
};
